import { useCallback } from "react";

import { cn } from "@/lib/chillnn/cn";
import type { SubOptionItemInput } from "@/lib/gql/__generated__/graphql";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";
import { QuestionSectionWrapper } from "@/components/features/items/edit/_shared/ui/components/system/question/_layouts/item-wrapper";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion/accordion";
import { CheckboxField } from "@/components/ui/form/fields/check-box-field/check-box";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";

import { TimePicker } from "./time-picker";

type Props = {
	subOption: ForAutoGenerator<SubOptionItemInput, "">;
	index: number;
	mainSectionClassName?: string;

	onSetSubOptionName: (name: string, index: number) => void;
	onSetIsShowTimeSchedule: (props: {
		isShowTimeSchedule: boolean;
		index: number;
		startAt: string;
		endAt: string;
	}) => void;
	onSetStartAt: (startAt: string, index: number) => void;
	onSetEndAt: (endAt: string, index: number) => void;
	onSetIsNextMorning: (isNextMorning: boolean, index: number) => void;
	onSetMaxDailyStockNum: (maxDailyStockNum: number | undefined, index: number) => void;
	onSetPriceAmount: (priceAmount: number | undefined, index: number) => void;
};

export const SubOptionItem = ({
	subOption,
	index,
	mainSectionClassName,
	onSetSubOptionName,
	onSetIsShowTimeSchedule,
	onSetStartAt,
	onSetEndAt,
	onSetIsNextMorning,
	onSetMaxDailyStockNum,
	onSetPriceAmount,
}: Props) => {
	const accordionValue = subOption.subOptionID?.value ?? `${index}`;
	const hasError = [
		subOption.name.error,
		subOption.maxDailyStockNum.error,
		subOption.price.amount.error,
		subOption.isShowTimeSchedule?.error,
		subOption.isNextMorning?.error,
		subOption.startAt?.error,
		subOption.endAt?.error,
	].some(Boolean);

	const handleChangeSubOptionName = useCallback(
		(name: string) => {
			onSetSubOptionName(name, index);
		},
		[onSetSubOptionName, index],
	);

	const handleChangeIsShowTimeSchedule = useCallback(
		(isShowTimeSchedule: boolean) => {
			onSetIsShowTimeSchedule({
				isShowTimeSchedule,
				index,
				startAt: subOption.startAt?.value ?? "",
				endAt: subOption.endAt?.value ?? "",
			});
		},
		[onSetIsShowTimeSchedule, index, subOption.startAt?.value, subOption.endAt?.value],
	);

	const handleChangeStartAt = useCallback(
		(startAt: string) => {
			onSetStartAt(startAt, index);
		},
		[onSetStartAt, index],
	);

	const handleChangeEndAt = useCallback(
		(endAt: string) => {
			onSetEndAt(endAt, index);
		},
		[onSetEndAt, index],
	);

	const handleChangeIsNextMorning = useCallback(
		(isNextMorning: boolean) => {
			onSetIsNextMorning(isNextMorning, index);
		},
		[onSetIsNextMorning, index],
	);

	const handleChangeMaxDailyStockNum = useCallback(
		(maxDailyStockNum: number | undefined) => {
			onSetMaxDailyStockNum(maxDailyStockNum, index);
		},
		[onSetMaxDailyStockNum, index],
	);

	const handleChangePriceAmount = useCallback(
		(priceAmount: number | undefined) => {
			onSetPriceAmount(priceAmount, index);
		},
		[onSetPriceAmount, index],
	);

	return (
		<div className={cn("relative flex flex-col gap-y-6", mainSectionClassName)}>
			<p className="font-semibold underline underline-offset-2">ID: {subOption.subOptionID?.value}</p>

			<QuestionSectionWrapper>
				<InputTextField
					className="w-full"
					label="サブオプション名"
					value={subOption.name.value}
					errorMessage={subOption.name.error}
					handleChange={handleChangeSubOptionName}
				/>
			</QuestionSectionWrapper>

			{/* 時間や料金の設定は縦に長いのでアコーディオンで折りたためるようにしている */}
			<Accordion
				type={"single"}
				collapsible
				defaultValue={
					hasError
						? // エラーがある場合はアコーディオンを開く
							accordionValue
						: undefined
				}
			>
				<AccordionItem value={accordionValue}>
					<AccordionTrigger>時間や料金の設定</AccordionTrigger>

					<AccordionContent>
						<div className="flex flex-col gap-y-12">
							<div className="flex flex-col gap-y-6">
								<div className="grid gap-2">
									<CheckboxField
										value={subOption.isShowTimeSchedule?.value ?? false}
										label="予約画面で時間帯を表示する"
										handleChange={handleChangeIsShowTimeSchedule}
										errorMessage={subOption.isShowTimeSchedule?.error}
									/>

									<p className="text-sm text-caption-50">
										予約画面で時間を表示する場合はチェックをつけてください。
										<br />
										「サブオプション」の名前に時間を含めている場合は不要です。
									</p>
								</div>

								<div className="">
									<div className="flex items-center gap-x-10">
										<QuestionSectionWrapper>
											<TimePicker
												label="開始時間"
												value={subOption.startAt?.value ?? ""}
												setValue={handleChangeStartAt}
												errorMessage={subOption.startAt?.error}
											/>
											<p>〜</p>
											<TimePicker
												label="終了時間"
												value={subOption.endAt?.value ?? ""}
												setValue={handleChangeEndAt}
												startAt={subOption.startAt?.value ?? ""}
												errorMessage={subOption.endAt?.error}
											/>
										</QuestionSectionWrapper>
									</div>
								</div>

								<div>
									<QuestionSectionWrapper>
										<CheckboxField
											value={subOption.isNextMorning?.value ?? false}
											label={"翌朝のオプションとして設定する"}
											handleChange={handleChangeIsNextMorning}
											errorMessage={subOption.isNextMorning?.error}
										/>
									</QuestionSectionWrapper>
								</div>
							</div>

							<div className="flex flex-col gap-y-4">
								<QuestionSectionWrapper>
									<p>１日あたり</p>
									<InputNumberField
										value={subOption.maxDailyStockNum.value}
										errorMessage={subOption.maxDailyStockNum.error}
										handleInput={handleChangeMaxDailyStockNum}
										hiddenSpinButton={false}
									/>
									<p>個まで販売可能</p>
								</QuestionSectionWrapper>
								<QuestionSectionWrapper>
									<p>１個あたり</p>
									<InputNumberField
										value={subOption.price.amount.value}
										errorMessage={subOption.price.amount.error}
										handleInput={handleChangePriceAmount}
									/>
									<p>円</p>
								</QuestionSectionWrapper>
							</div>
						</div>
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		</div>
	);
};

SubOptionItem.displayName = "SubOptionItem";
